import { watch } from 'vue'

const ACTIVE_GENERATION_MAX_AGE_MS = 60 * 60 * 1000

/** @type {Map<string, { hydrated: boolean, watchersInstalled: boolean }>} */
const clientFlagsBySlug = new Map()

function getClientFlags(slug) {
  if (!clientFlagsBySlug.has(slug)) {
    clientFlagsBySlug.set(slug, { hydrated: false, watchersInstalled: false })
  }
  return clientFlagsBySlug.get(slug)
}

function readJsonSessionStorage(key) {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function writeJsonSessionStorage(key, value) {
  if (typeof sessionStorage === 'undefined') return
  try {
    if (value == null) {
      sessionStorage.removeItem(key)
      return
    }
    sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* private mode / quota */
  }
}

function normalizePromptByTask(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const next = {}
  for (const [taskId, prompt] of Object.entries(raw)) {
    const key = String(taskId || '').trim()
    if (!key) continue
    if (typeof prompt !== 'string') continue
    if (!prompt) continue
    next[key] = prompt
  }
  return next
}

function normalizeActiveGeneration(raw, options = {}) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null

  const {
    requireTaskId = false,
    maxAgeMs = 0,
  } = options
  const taskId = String(raw.taskId || '').trim()
  const prompt = typeof raw.prompt === 'string' ? raw.prompt : ''
  const payload =
    raw.payload && typeof raw.payload === 'object' && !Array.isArray(raw.payload)
      ? { ...raw.payload }
      : null
  const startedAt = Number(raw.startedAt)
  const normalizedStartedAt = Number.isFinite(startedAt) && startedAt > 0 ? startedAt : Date.now()

  if (requireTaskId && !taskId) return null
  if (!taskId && !payload) return null
  if (maxAgeMs > 0 && Date.now() - normalizedStartedAt > maxAgeMs) return null

  return {
    taskId,
    prompt,
    payload: payload || {},
    startedAt: normalizedStartedAt,
  }
}

/**
 * Per-variant session persistence (active generation + prompt map).
 * Each slug gets isolated useState keys and sessionStorage entries.
 */
export function createTalkingGenerationSessionState(slug) {
  const activeGenerationStateKey = `${slug}-active-generation-state`
  const promptByTaskStateKey = `${slug}-prompt-by-task-state`
  const activeGenerationStorageKey = `${slug}-active-generation-state`
  const promptByTaskStorageKey = `${slug}-prompt-by-task-state`

  return function useTalkingGenerationSessionState() {
    const flags = getClientFlags(slug)
    const activeGeneration = useState(activeGenerationStateKey, () => null)
    const promptByTaskId = useState(promptByTaskStateKey, () => ({}))

    function persistActiveGeneration() {
      if (!import.meta.client) return
      writeJsonSessionStorage(
        activeGenerationStorageKey,
        normalizeActiveGeneration(activeGeneration.value, { requireTaskId: true }),
      )
    }

    function persistPromptByTask() {
      if (!import.meta.client) return
      const normalized = normalizePromptByTask(promptByTaskId.value)
      writeJsonSessionStorage(
        promptByTaskStorageKey,
        Object.keys(normalized).length ? normalized : null,
      )
    }

    if (import.meta.client && !flags.hydrated) {
      flags.hydrated = true
      const storedActiveGeneration = readJsonSessionStorage(activeGenerationStorageKey)
      activeGeneration.value = normalizeActiveGeneration(
        storedActiveGeneration,
        {
          requireTaskId: true,
          maxAgeMs: ACTIVE_GENERATION_MAX_AGE_MS,
        },
      )
      if (storedActiveGeneration && !activeGeneration.value) {
        writeJsonSessionStorage(activeGenerationStorageKey, null)
      }
      promptByTaskId.value = normalizePromptByTask(
        readJsonSessionStorage(promptByTaskStorageKey),
      )
    }

    if (import.meta.client && !flags.watchersInstalled) {
      flags.watchersInstalled = true
      watch(activeGeneration, () => persistActiveGeneration(), { deep: true })
      watch(promptByTaskId, () => persistPromptByTask(), { deep: true })
    }

    function setActiveGeneration(next) {
      activeGeneration.value = normalizeActiveGeneration(next)
      persistActiveGeneration()
    }

    function patchActiveGeneration(partial = {}) {
      const current = normalizeActiveGeneration(activeGeneration.value) || {
        taskId: '',
        prompt: '',
        payload: {},
        startedAt: Date.now(),
      }

      const mergedPayload =
        partial.payload && typeof partial.payload === 'object' && !Array.isArray(partial.payload)
          ? { ...current.payload, ...partial.payload }
          : current.payload

      setActiveGeneration({
        ...current,
        ...partial,
        payload: mergedPayload,
      })
    }

    function clearActiveGeneration() {
      activeGeneration.value = null
      persistActiveGeneration()
    }

    function setPromptForTask(taskId, prompt) {
      const key = String(taskId || '').trim()
      if (!key || typeof prompt !== 'string' || !prompt) return
      promptByTaskId.value = {
        ...promptByTaskId.value,
        [key]: prompt,
      }
      persistPromptByTask()
    }

    function removePromptForTask(taskId) {
      const key = String(taskId || '').trim()
      if (!key || !promptByTaskId.value[key]) return
      const next = { ...promptByTaskId.value }
      delete next[key]
      promptByTaskId.value = next
      persistPromptByTask()
    }

    return {
      activeGeneration,
      promptByTaskId,
      setActiveGeneration,
      patchActiveGeneration,
      clearActiveGeneration,
      setPromptForTask,
      removePromptForTask,
    }
  }
}
